import { useMemo } from 'react';
import {
  components,
  type OptionProps,
  type SingleValueProps,
} from 'react-select';

import { Box } from '@hivebrite/design-system/Box';
import { Image } from '@hivebrite/design-system/Image';
import { ReactSelectAsyncInput } from '@hivebrite/design-system/Inputs';
import type { ReactSelectInputProps } from '@hivebrite/design-system/Inputs';

import { makeFetchTerm } from 'hb-react/shared/components/DesignSystem/Inputs/ReactSelect/utils';
import { axiosWithSyntaxHandlers } from 'hb-react/shared/utils/axios';

const noOptionsMessage = ({ inputValue }) =>
  inputValue
    ? __('User %{user} not found').i__({
        user: inputValue,
      })
    : __('Search for a user');

const CustomSingleValueComponent = (
  props: SingleValueProps<{
    data: {
      photo_url: string;
      headline?: string;
    };
  }>,
) => {
  const { data, children, options = [] } = props;

  const user = useMemo(
    () =>
      // @ts-expect-error: fix types
      data?.data || options.find((option) => option.value === data.value)?.data,
    [data, options],
  );

  return (
    <components.SingleValue {...props}>
      <Box
        alignItems="center"
        justifyContent="space-between"
        data-testid="react-select-option"
      >
        <Box justifyContent="center">
          {user?.photo_url && (
            <Box mr={3}>
              <Image src={user.photo_url} width={24} height={24} />
            </Box>
          )}
          <Box flexDirection="column" justifyContent="center">
            <Box font="body-regular-black" color="black">
              {children}
            </Box>
          </Box>
        </Box>
      </Box>
    </components.SingleValue>
  );
};

const CustomOptionComponent = (
  props: OptionProps<{
    id: number;
    data: {
      photo_url: string;
      headline?: string;
    };
  }>,
) => {
  const { data, isFocused, isSelected, children, options } = props;
  const { id, data: user } = data;

  return (
    <li
      aria-posinset={id}
      aria-selected={isSelected}
      aria-setsize={options.length}
      data-testid="react-select-option"
      role="option"
      tabIndex={-1}
    >
      <components.Option {...props}>
        <Box alignItems="center" justifyContent="space-between">
          <Box justifyContent="center">
            {user?.photo_url && (
              <Box mr={3}>
                <Image src={user.photo_url} width={32} height={32} />
              </Box>
            )}
            <Box flexDirection="column" justifyContent="center">
              <Box
                font="body-regular-black"
                color={isFocused ? 'white' : 'black'}
              >
                {children}
              </Box>
              {user?.headline && (
                <Box
                  font="metadata-semibold-asphalt"
                  color={isFocused ? 'white' : 'dove'}
                  mt={1}
                >
                  {user.headline}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </components.Option>
    </li>
  );
};

export interface ReactSelectMentoringProfileProps {
  searchType: 'mentors' | 'mentees';
  placeholder?: string;
  inputValue?: string;
  defaultOptions?: boolean;
  withAvatars?: boolean;
  networkId: number;
  programId: number;
  topicId?: number;
  [key: string]: any; // ReactSelectAsyncInput props type to add
}

const ReactSelectMentoringProfile = ({
  searchType,
  placeholder,
  inputValue,
  defaultOptions = false,
  withAvatars = false,
  networkId,
  programId,
  topicId,
  ...otherProps
}: ReactSelectMentoringProfileProps) => {
  const customComponents = useMemo(
    () =>
      withAvatars
        ? {
            Option: CustomOptionComponent,
            SingleValue: CustomSingleValueComponent,
          }
        : {},
    [withAvatars],
  ) as ReactSelectInputProps<
    OptionProps & { value: string | number },
    boolean,
    any
  >['components'];

  return (
    <ReactSelectAsyncInput
      {...otherProps}
      components={customComponents}
      placeholder={placeholder}
      defaultOptions={defaultOptions}
      noOptionsMessage={noOptionsMessage}
      id="select-mentoring-profile"
      name="select-mentoring-profile"
      loadOptions={makeFetchTerm({
        url:
          searchType === 'mentors'
            ? Routes.backoffice_network_mentorings_program_can_be_a_mentor_users_path(
                networkId,
                programId,
              )
            : Routes.backoffice_network_mentorings_program_can_be_a_mentee_users_path(
                networkId,
                programId,
              ),
        params: {
          orber_by: 'lastname',
          query: { keyword: inputValue },
          topicId,
          limit: 30,
        },
        paramsTransformer: ({ params, inputValue }) => ({
          ...params,
          query: {
            keyword: inputValue || params.keyword,
          },
        }),
        client: axiosWithSyntaxHandlers,
      })}
    />
  );
};

export default ReactSelectMentoringProfile;
